import { useMemo } from 'react'

import { REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { useGetChecklistQuery } from '~/redux/apis/reward-api'
import { CheckListItemType, CheckListTypes } from '~/redux/apis/types'
import useCurrentUser from '~/hooks/useCurrentUser'

interface ResponseType {
  isSuccess: boolean
  isLoading: boolean
  data: CheckListTypes | undefined
  refetch: () => void
}

// Verifying the email is no longer part of the checklist. The API stops sending it too; dropping it here as
// well keeps the progress bar and chest reaching 100% without it while an older API is still deployed.
const HIDDEN_ITEMS: string[] = [REWARD_FEATURE.VERIFY_EMAIL]

const stripHiddenItems = (checklist: CheckListTypes | undefined): CheckListTypes | undefined => {
  if (!checklist) return checklist

  return Object.fromEntries(Object.entries(checklist).filter(([itemKey]) => !HIDDEN_ITEMS.includes(itemKey)))
}

const useGetChecklist = (): ResponseType => {
  const user = useCurrentUser()
  const { isSuccess, data, isLoading, refetch } = useGetChecklistQuery(undefined, { skip: !user })
  const checklist = useMemo(() => stripHiddenItems(data), [data])

  return { isSuccess, data: checklist, isLoading, refetch }
}

const useChecklistItem = (checklistItemKey: keyof CheckListTypes): CheckListItemType | null => {
  const { isSuccess, data: checklist } = useGetChecklist()
  return isSuccess && checklist?.[checklistItemKey] ? checklist[checklistItemKey] : null
}

export { useGetChecklist, useChecklistItem }
